import React, { useEffect, useRef, useState } from "react";
import { useGetConfigQuery } from "@diditusa/shared/api/configApi";
import { useExchangeCodeForTokenMutation } from "@diditusa/shared/api/sessionApi";
import { AuthState } from "@diditusa/shared/types/authTypes";

import { Outlet, useLocation, useNavigate } from "react-router-dom";
import LoadingIndicator from "@diditusa/shared/web_components/LoadingIndicator/loadingIndicator";

import {
  CognitoUserPool,
  CognitoUser,
  CognitoUserSession,
  CognitoIdToken,
  CognitoAccessToken,
  CognitoRefreshToken,
  ICognitoUserPoolData,
} from "amazon-cognito-identity-js";
import {
  setAuthState,
  updateUserFromCachedSession,
} from "@diditusa/shared/reducers/SessionSlice";

import "./appContainer.css";
import { useAppSelector, useAppDispatch } from "../../hooks";
import { LoadState, UserRoles } from "@diditusa/shared/types/commonTypes";
import Sidebar from "@diditusa/shared/web_components/Sidebar/Sidebar";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PersonIcon from "@mui/icons-material/Person";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import SecurityIcon from "@mui/icons-material/Security";
import SellIcon from "@mui/icons-material/Sell";
function AppContainer() {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);

  const authCode = searchParams.get("code");
  const appState = searchParams.get("appState");

  const [
    exchangeCodeForToken,
    { isLoading: isExchanging, error: exchangeError },
  ] = useExchangeCodeForTokenMutation();
  const {
    data: config,
    isLoading: isConfigLoading,
    isError: isConfigError,
  } = useGetConfigQuery();
  const userPoolRef = useRef<CognitoUserPool>(null);
  const userRef = useRef<CognitoUser>(null);
  const sessionState = useAppSelector((state) => state.root.sessionState);

  useEffect(() => {
    if (isConfigLoading) return;

    if (isConfigError) {
      console.error("Error loading config");
      return;
    }

    const poolData: ICognitoUserPoolData = {
      UserPoolId: config?.userPoolId || "",
      ClientId: config?.userPoolClientId || "",
    };

    if (!process.env.NODE_ENV || process.env.NODE_ENV === "development") {
      poolData.endpoint = process.env.REACT_APP_COGNITO_ENDPOINT;
    }
    userPoolRef.current = new CognitoUserPool(poolData);

    loadSession();
  }, [config, isConfigError, isConfigLoading]);

  useEffect(() => {
    const loginUrl = `${config?.userLoginUrl}&redirect_uri=${getRedirectUri()}`;
    if (sessionState.authState === AuthState.UNAUTHENTICATED && config) {
      console.log("user unauth, redirecting");
      if (userRef.current) {
        userRef.current.signOut(() => {
          window.location.href = loginUrl;
        });
      } else {
        window.location.href = loginUrl;
      }
    }
  }, [sessionState.authState, config]);

  const loadSession = () => {
    if (!userPoolRef.current) {
      console.error("UserPool undefined");
      return;
    }

    const user = userPoolRef.current.getCurrentUser();

    if (user !== null) {
      user.getSession((err: Error | null, session: CognitoUserSession) => {
        if (err === null && session.isValid()) {
          dispatch(updateUserFromCachedSession(session));
        } else {
          console.log("Error getting session", err);
          user.signOut();
          dispatch(setAuthState(AuthState.UNAUTHENTICATED));
        }
      });
      return;
    }

    if (authCode && config) {
      // If user is null and we have auth code then we likely arrived here after being redirected
      // from the sign in hosted UI. Need to exchange code for tokens.
      console.debug(
        "user is null and we have auth code, need to exchange",
        authCode
      );
      const exchangeRequest = {
        clientId: config.userPoolClientId || "",
        code: authCode,
        redirectUrl: getRedirectUri(),
        state: "someState",
      };
      exchangeCodeForToken(exchangeRequest);
    } else {
      console.log(
        "No user and no auth code. Need to send them to sign in page"
      );
      dispatch(setAuthState(AuthState.UNAUTHENTICATED));
    }
  };

  useEffect(() => {
    if (sessionState.loadState === LoadState.LOADED) {
      // This happens after the sign in flow (vs using existing session flow)
      const IdToken = new CognitoIdToken({
        IdToken: sessionState.user.idToken || "",
      });
      const AccessToken = new CognitoAccessToken({
        AccessToken: sessionState.user.accessToken || "",
      });
      const RefreshToken = new CognitoRefreshToken({ RefreshToken: "" });
      const session = new CognitoUserSession({
        IdToken,
        AccessToken,
        RefreshToken,
      });

      userRef.current = new CognitoUser({
        Username: sessionState.user.username || "",
        Pool: userPoolRef.current!,
      });
      userRef.current.setSignInUserSession(session);

      if (sessionState.user.role !== UserRoles.ADMIN) {
        userRef.current.signOut(() => {
          window.location.href = "https://saef.app";
        });
      }
    }
  }, [sessionState.loadState, sessionState.user]);

  const getRedirectUri = () => {
    return `${window.location.origin}`;
  };

  const showContent = () =>
    !isConfigLoading &&
    !isConfigError &&
    sessionState.user.role === UserRoles.ADMIN &&
    sessionState.authState === AuthState.AUTHENTICATED;

  // todo: move these up to App.ts
  const [navItems, setNavItems] = useState([
    { label: "Dashboard", icon: <DashboardIcon />, path: "/", active: true },
    { label: "Upcoming Jobs", icon: <SecurityIcon />, path: "/jobs/ready" },
    {
      label: "Completed Jobs",
      icon: <SecurityIcon />,
      path: "/jobs/completed",
    },
    { label: "Leads", icon: <SecurityIcon />, path: "/jobs/abandoned" },
    { label: "Customers", icon: <PersonIcon />, path: "/users" },
    { label: "Products", icon: <SellIcon />, path: "/products" },
    { label: "Company", icon: <CorporateFareIcon />, path: "/companies" },
    { label: "Affiliates", icon: <CorporateFareIcon />, path: "/affiliates" },
  ]);

  const onNavItemClick = (idx: number) => {
    setNavItems(
      navItems.map((ni, niIdx) => ({ ...ni, active: idx === niIdx }))
    );
    navigate(navItems[idx].path);
  };

  const logout = () => {
    dispatch(setAuthState(AuthState.UNAUTHENTICATED));
  };

  return (
    <div className="page-layout">
      {showContent() && (
        <Sidebar
          navItems={navItems}
          onNavItemClick={onNavItemClick}
          logout={logout}
        />
      )}
      <LoadingIndicator isVisible={isConfigLoading || isExchanging} />
      {showContent() && <Outlet />}
      {isConfigError && <div>Error loading configuration.</div>}
      {exchangeError && <div>Error during code exchange.</div>}
    </div>
  );
}

export default AppContainer;
